import { useFormik } from 'formik'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'

type FormValues = {
  name: string
  selectedProgramId: string
  taskTemplateIds: string[]
  countries: string[]
  accountIds: string[]
  nominatorGroups: string[]
}

type UseAssignmentRuleFormParams = {
  allCountries: { label: string; value: string }[]
  onSubmit: (values: FormValues) => void
}

export const useAssignmentRuleForm = ({ allCountries, onSubmit }: UseAssignmentRuleFormParams) => {
  const { t } = useTranslation()

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        name: Yup.string()
          .max(
            255,
            t('errorMessage.orgFieldCharacterLimit', {
              fieldName: t('tasksAutoAssignmentRule.form.name.label')
            })
          )
          .min(
            1,
            t('errorMessage.orgFieldCharacterLimit', {
              fieldName: t('tasksAutoAssignmentRule.form.name.label')
            })
          )
          .trim()
          .required(t('errorMessage.required')),
        selectedProgramId: Yup.string().required(t('errorMessage.required')),
        taskTemplateIds: Yup.array()
          .of(Yup.string())
          .min(1, t('errorMessage.required'))
          .required(t('errorMessage.required')),
        countries: Yup.array().of(Yup.string()).nullable().optional().default([]),
        accountIds: Yup.array().of(Yup.string()).nullable().optional().default([]),
        nominatorGroups: Yup.array().of(Yup.string()).nullable().optional().default([])
      }),
    [t]
  )

  const form = useFormik<FormValues>({
    validateOnMount: false,
    enableReinitialize: false,
    initialValues: {
      name: '',
      selectedProgramId: '',
      taskTemplateIds: [],
      countries: [],
      accountIds: [],
      nominatorGroups: []
    },
    validationSchema,
    onSubmit: values => {
      const payload = {
        ...values,
        countries: values.countries.length > 0 ? values.countries : allCountries.map(c => c.value)
      }
      onSubmit(payload)
    }
  })

  const resetDependentFields = () => {
    form.setFieldValue('taskTemplateIds', [])
    form.setFieldValue('accountIds', [])
    form.setFieldValue('nominatorGroups', [])
  }

  return {
    form,
    resetDependentFields
  }
}
